import { useSupportContext } from "@/stores";
import { Attachment } from "@/types";
import { AttachFile } from "@mui/icons-material";
import { CircularProgress, Stack } from "@mui/material";
import { ChangeEvent, FC, useRef } from "react";
import { useNotify } from "react-admin";
import { useUploadImage, useUploadVideo } from "../../queries";

const FILE_MAX_SIZE_MB = 15;

const FileInput: FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const notify = useNotify();
  const { currentChat } = useSupportContext();

  const { uploadImage, isUploadImageLoading } = useUploadImage();
  const { uploadVideo, isUploadVideoLoading } = useUploadVideo();

  const onUploadFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  const onChangeInputHandler = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!currentChat) {
      return notify("Select any chat before sending file...", {
        type: "error",
      });
    }

    if (file) {
      const fileSizeInMB = file.size / (1024 * 1024);

      if (fileSizeInMB > FILE_MAX_SIZE_MB) {
        notify(`Max file size is ${FILE_MAX_SIZE_MB}MB.`, { type: "error" });
        return;
      }

      const fileType = ((file.type.startsWith("image/") && "image") ||
        (file.type.startsWith("video/") && "video")) as Attachment["type"];

      switch (fileType) {
        case "image": {
          uploadImage({
            imageFile: file,
            chatId: currentChat.id,
          });
          break;
        }

        case "video": {
          uploadVideo({
            videoFile: file,
            chatId: currentChat.id,
          });
          break;
        }
      }
    }
    event.target.value = "";
  };

  return (
    <Stack
      justifyContent="center"
      alignItems="center"
      role="button"
      tabIndex={0}
      onClick={onUploadFile}
    >
      <input
        ref={fileInputRef}
        hidden
        type="file"
        accept="image/*,video/*"
        multiple={false}
        onChange={onChangeInputHandler}
      />
      {isUploadImageLoading || isUploadVideoLoading ? (
        <CircularProgress size={16} />
      ) : (
        <AttachFile sx={{ cursor: "pointer" }} />
      )}
    </Stack>
  );
};

export { FileInput };
